import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { experiences } from '../data';
import {
  FACILITATION_OPTIONS,
  FOOD_OPTIONS,
  PLANNING_SUPPORT_OPTIONS,
  QUOTE_BUILDER_EXCLUDED_VENDOR_IDS,
  SERVICE_CHARGE_ALLOWANCE_RATE,
  TAX_RATES,
  rateFor,
} from '../quote/pricingModel';
import {
  buildQuote as buildQuoteFromIds,
  eligibleVendors,
  formatMoney,
  formatRange,
  isActiveListing,
} from '../quote/quoteEngine';
import {
  decodeQuote,
  encodeQuote,
  loadQuote,
  planRequestUrl,
  saveQuote,
} from '../quote/quoteRef';
import {
  FOOD_DISPLAY_ORDER,
  PRICE_SOURCE_LABELS,
  SORT_MODES,
  countPackageVendors,
  foodDisplayCategory,
  foodSizeFitBand,
  priceSourceFor,
  sortOptions,
  vendorSizeFitBand,
} from '../quote/builderHelpers';
import {
  DENVER_VENUES,
  DENVER_VENUE_AREAS,
} from '../venues/denverVenues';
import {
  filterDenverVenues,
  formatVenuePrice,
} from '../venues/venueCatalog';
import { buildQuoteDocument } from '../quote/quoteDocument';
import quoteDocumentLogo from '../images/logowithnameandphrase.png';
import './QuoteBuilderPage.css';

// Adapt the builder's selected objects to the shared quote engine.
function buildQuote({ venue, venues, vendors = [], foodOptions = [], facilitation,
  planningSupport, ...input }) {
  const selectedVenues = venues || (venue ? [venue] : []);
  const quote = buildQuoteFromIds({
    ...input,
    venueIds: selectedVenues.map((item) => item.id),
    vendorIds: vendors.map((item) => item.id),
    foodIds: foodOptions.map((item) => item.id),
    facilitationId: facilitation?.id || 'none',
    planningSupportId: planningSupport?.id || 'none',
  }, vendors, selectedVenues);
  return {
    ...quote,
    lines: quote.lines.map((line, index) => ({
      ...line,
      id: `${line.kind}-${line.venueId || line.vendorId || line.optionId || index}`,
      type: line.kind === 'activity' ? 'vendor' : line.kind === 'planningSupport' ? 'planning' : line.kind,
      sourceId: line.venueId || line.vendorId || line.optionId,
      optional: ['venue', 'activity', 'food', 'facilitation', 'planningSupport'].includes(line.kind),
    })),
  };
}

const CITY = 'Denver';
const STATE = 'CO';

const HEADCOUNT_MIN = 5;
const HEADCOUNT_MAX = 200;
const PER_PERSON_BUDGET_MIN = 25;
const PER_PERSON_BUDGET_MAX = 500;
const DEFAULT_PER_PERSON_BUDGET = 150;
const PER_PERSON_BUDGET_STEP = 5;
const VENUES_PER_PAGE = 8;
const RECOMMENDATIONS_PER_SECTION = 4;
const FOOD_RECOMMENDATION_GROUPS = [
  {
    label: 'Light bites',
    ids: [
      'three-tomatoes-happy-hour',
      'mhc-appetizer-bundle-2',
      'bagel-deli-cocktail-meat-tray',
      'risica-burrata-bar',
    ],
  },
  {
    label: 'Catered lunch',
    ids: [
      'etais-corporate-box',
      'bagel-deli-box-lunch',
      'mhc-gourmet-wrap-box',
    ],
  },
  {
    label: 'Full meal',
    ids: [
      'three-tomatoes-fiesta',
      'brothers-two-meat',
      'spice-room-essentials',
      'tocabe-indigenous-grains',
      'mhc-fajita-bar',
    ],
  },
];
const FOOD_OPTIONS_BY_ID = new Map(
  FOOD_OPTIONS.map((option) => [
    option.id,
    option,
  ])
);
const DINING_CATEGORY =
  'Dining & Culinary Experiences';
const HOST_CATEGORY =
  'Speakers & Event Hosts';
const HOST_OPTION =
  FACILITATION_OPTIONS.find(
    (option) => option.id === 'host'
  );

const ACTIVITY_CATEGORY_ORDER = [
  DINING_CATEGORY,
  'Games, Challenges & Competition',
  'Active & Outdoor Adventures',
  'Arts, Culture & Creative Experiences',
  'Entertainment & Social Events',
  'Team Building & Communication',
  'Training & Professional Development',
  HOST_CATEGORY,
  'Volunteer & Purpose-Driven Events',
  'Wellness & Retreat Experiences',
];

const ACTIVITY_CATEGORY_MAP = {
  'Food & Drinks':
    DINING_CATEGORY,
  'Dining & Culinary Experiences':
    DINING_CATEGORY,

  'Active Games':
    'Games, Challenges & Competition',
  'Escape Room':
    'Games, Challenges & Competition',
  Games:
    'Games, Challenges & Competition',
  'Games, Challenges & Competition':
    'Games, Challenges & Competition',
  'Scavenger Hunt':
    'Games, Challenges & Competition',

  Active:
    'Active & Outdoor Adventures',
  Hiking:
    'Active & Outdoor Adventures',
  Outdoor:
    'Active & Outdoor Adventures',
  'Park & Picnic':
    'Active & Outdoor Adventures',
  'Walking & Biking':
    'Active & Outdoor Adventures',
  'Walking Trail':
    'Active & Outdoor Adventures',
  'Active & Outdoor Adventures':
    'Active & Outdoor Adventures',

  Creative:
    'Arts, Culture & Creative Experiences',
  Culture:
    'Arts, Culture & Creative Experiences',
  'Arts, Culture & Creative Experiences':
    'Arts, Culture & Creative Experiences',

  Entertainment:
    'Entertainment & Social Events',
  'Entertainment & Social Events':
    'Entertainment & Social Events',

  'Team Building':
    'Team Building & Communication',
  'Team Communication':
    'Team Building & Communication',
  'Team Building & Communication':
    'Team Building & Communication',

  'Communication Training':
    'Training & Professional Development',
  'Customized Business Training':
    'Training & Professional Development',
  'Leadership and Communication':
    'Training & Professional Development',
  'Leadership and Team Development':
    'Training & Professional Development',
  'Leadership Training':
    'Training & Professional Development',
  'Professional Development':
    'Training & Professional Development',
  'Sales Training':
    'Training & Professional Development',
  'Team Building Training':
    'Training & Professional Development',
  'Training & Professional Development':
    'Training & Professional Development',

  'AI and Innovation Speaker':
    HOST_CATEGORY,
  'Business Growth Speaker':
    HOST_CATEGORY,
  'Communication and Leadership Speaker':
    HOST_CATEGORY,
  'Communication Speaker':
    HOST_CATEGORY,
  'Leadership Speaker':
    HOST_CATEGORY,
  'Motivational Speaker':
    HOST_CATEGORY,
  'Speaker / Emcee / Entertainment':
    HOST_CATEGORY,
  'Speaker Matching Service':
    HOST_CATEGORY,
  'Wellness and Motivational Speaker':
    HOST_CATEGORY,
  'Workplace Culture Speaker':
    HOST_CATEGORY,
  'Speakers & Keynotes':
    HOST_CATEGORY,
  'Speakers, Hosts & Entertainment':
    HOST_CATEGORY,

  'Environmental Volunteer Event':
    'Volunteer & Purpose-Driven Events',
  'Purpose-Driven':
    'Volunteer & Purpose-Driven Events',
  'Virtual Volunteer Event':
    'Volunteer & Purpose-Driven Events',
  Volunteer:
    'Volunteer & Purpose-Driven Events',
  'Volunteer & Purpose-Driven Events':
    'Volunteer & Purpose-Driven Events',

  'Botanical Retreat Venue':
    'Wellness & Retreat Experiences',
  'Creative Retreat Venue':
    'Wellness & Retreat Experiences',
  'Downtown Retreat Venue':
    'Wellness & Retreat Experiences',
  'Executive Board Retreat':
    'Wellness & Retreat Experiences',
  'Facilitated Virtual Retreat':
    'Wellness & Retreat Experiences',
  'Full-Service Retreat Hotel':
    'Wellness & Retreat Experiences',
  'Hybrid Executive Retreat':
    'Wellness & Retreat Experiences',
  'Mountain Retreat Venue':
    'Wellness & Retreat Experiences',
  'Riverside Retreat':
    'Wellness & Retreat Experiences',
  'Workplace Wellness':
    'Wellness & Retreat Experiences',
  Workspace:
    'Wellness & Retreat Experiences',
  'Wellness & Retreat Experiences':
    'Wellness & Retreat Experiences',
};

function activityCategoryFor(vendor) {
  return (
    ACTIVITY_CATEGORY_MAP[
      vendor && vendor.category
    ] ||
    'Team Building & Communication'
  );
}

const BUILDER_STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'venue', label: 'Venue' },
  { key: 'experience', label: 'Experience' },
  { key: 'food', label: 'Food & drinks' },
  { key: 'planning', label: 'Planning' },
];

const VENDOR_COORDINATION_OPTION =
  PLANNING_SUPPORT_OPTIONS.find(
    (option) => option.id === 'vendor-coordination'
  );

const FOOD_FAVORITE_IDS = new Set(
  FOOD_RECOMMENDATION_GROUPS.flatMap((group) => group.ids)
);

// Venue cards show the dollar range itself; the source tag says whether it
// is a published rate or a Teamward estimate.
function venuePriceText(venue) {
  if (!venue) return '';
  if (venue.priceHigh === 0) {
    return venue.priceLabel || 'No venue rental cost';
  }
  return formatVenuePrice(venue) || venue.priceLabel || '';
}

function venuePriceSource(venue) {
  return priceSourceFor(venue && venue.priceConfidence, {
    free: Boolean(venue) && venue.priceHigh === 0,
  });
}

function PriceSourceTag({ source }) {
  if (!source) return null;
  return (
    <span className={`qb-price-source qb-price-source-${source}`}>
      {PRICE_SOURCE_LABELS[source]}
    </span>
  );
}

function SortControl({ id, value, onChange, headcount }) {
  return (
    <label className="qb-sort-control" htmlFor={id}>
      <span>Sort options</span>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {SORT_MODES.map((mode) => (
          <option key={mode.id} value={mode.id}>
            {mode.id === 'popular'
              ? `Popular for groups of ${headcount}`
              : mode.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const ACTIVITY_LOCATION_VENUE_ID =
  'activity-location';

// Used only when an activity already
// includes its own space (axe throwing,
// escape rooms, tours, volunteer sites).
const ACTIVITY_LOCATION_VENUE = {
  id: ACTIVITY_LOCATION_VENUE_ID,
  name: 'At the activity location',
  category: 'No separate venue needed',
  area: null,
  address: null,
  capacity: 10000,
  isOwnSpace: true,
  outdoor: false,
  catering:
    'Use the activity provider’s food options or bring in catering',
  includesFood: false,
  priceLow: 0,
  priceHigh: 0,
  priceBasis: 'per_event',
  priceConfidence: 'confirmed',
  priceLabel: 'Included with the activity',
  pricingMethod:
    'The selected activity provides its own space',
  reviewedOn: '2026-09-28',
};

const QUOTE_VENUES = [
  ...DENVER_VENUES,
  ACTIVITY_LOCATION_VENUE,
];

// Speakers, trainers, and a few
// flexible formats come to you.
const BRING_TO_YOU_CATEGORIES = new Set([
  'Training & Professional Development',
  HOST_CATEGORY,
]);

const BRING_TO_YOU_VENDOR_IDS = new Set([
  'denver-this-is-a-clue-self-hosted',
  'denver-this-is-a-clue-hosted',
  'denver-this-is-a-clue-custom',
  'denver-lets-roam-large-group-scavenger-hunt',
]);

function hasOwnVenue(vendor) {
  if (!vendor) {
    return false;
  }

  if (
    BRING_TO_YOU_VENDOR_IDS.has(vendor.id) ||
    BRING_TO_YOU_CATEGORIES.has(
      activityCategoryFor(vendor)
    )
  ) {
    return false;
  }

  return !/virtual|hybrid/i.test(
    vendor.setting || ''
  );
}

// Explicit fixed-location retreats stay off-site even if a hybrid format exists.
function isOfficeActivity(vendor) {
  if (/Venue|Executive Board|Hybrid Executive Retreat/i.test(vendor.category || '')) {
    return false;
  }
  return !hasOwnVenue(vendor);
}

function isOfficeFood(option) {
  // The current food catalog consists of catering, pickup and delivery menus.
  return option.officeCompatible !== false && !option.requiresOffsite;
}

function OfficeOptions({ options, officeHosted, compatible, children }) {
  const visible = officeHosted ? options.filter(compatible) : options;
  const hidden = officeHosted ? options.filter((option) => !compatible(option)) : [];

  return (
    <>
      {visible.map(children)}
      {hidden.length > 0 && (
        <details className="qb-office-hidden">
          <summary>
            {hidden.length} option{hidden.length === 1 ? '' : 's'} hidden · Require an off-site location
          </summary>
          <p>Hidden because you selected “Host at your office”. You can still explore these alternatives.</p>
          {hidden.map(children)}
        </details>
      )}
    </>
  );
}

const KEYNOTE_OPTION = FACILITATION_OPTIONS.find((option) => option.id === 'speaker');
const KEYNOTE_RANGE_LABEL = KEYNOTE_OPTION
  ? `$${KEYNOTE_OPTION.low.toLocaleString()}–$${KEYNOTE_OPTION.high.toLocaleString()}`
  : '$5,000–$25,000';

// Only starters that fit the visitor's budget are shown. Order matters:
// the first fitting starters appear first.
const STARTER_PACKAGES = [
  {
    id: 'office-trivia-lunch',
    label: 'Office trivia & lunch',
    detail: 'Hosted trivia by This Is A Clue + catered box lunch',
    venueId: null,
    vendorIds: ['denver-this-is-a-clue-hosted'],
    foodIds: ['etais-corporate-box'],
  },
  {
    id: 'team-inspiration-day',
    label: 'Team inspiration day',
    detail: 'An engaging keynote + lunch + time to connect.',
    budgetNote: `Keynote speakers typically run ${KEYNOTE_RANGE_LABEL}.`,
    venueId: null,
    vendorIds: ['denver-brad-montgomery'],
    foodIds: ['etais-corporate-box'],
  },
  {
    id: 'lunch-and-learn',
    label: 'Office lunch & learn',
    detail: 'Workshop + catered box lunch',
    venueId: null,
    vendorIds: [
      'denver-zoe-training-lunch-and-learn',
    ],
    foodIds: ['etais-corporate-box'],
  },
  {
    id: 'activity-outing',
    label: 'Team activity outing',
    detail: 'Axe throwing at its own venue',
    venueId: ACTIVITY_LOCATION_VENUE_ID,
    vendorIds: ['denver-bad-axe-throwing'],
    foodIds: [],
  },
  {
    id: 'volunteer-day',
    label: 'Volunteer day',
    detail: 'Group shift at Food Bank of the Rockies',
    venueId: ACTIVITY_LOCATION_VENUE_ID,
    vendorIds: [
      'denver-food-bank-rockies-group-volunteer',
    ],
    foodIds: [],
  },
  {
    id: 'half-day-retreat',
    label: 'Half-day retreat',
    detail: 'Garden retreat + DiSC workshop + lunch',
    venueId: ACTIVITY_LOCATION_VENUE_ID,
    vendorIds: [
      'denver-botanic-gardens-corporate-retreat',
      'denver-training-group-disc-workshop',
    ],
    foodIds: ['three-tomatoes-fiesta'],
  },
];

function groupByCategory(vendors) {
  const groups = new Map();

  vendors.forEach((vendor) => {
    const key =
      activityCategoryFor(vendor);

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key).push(vendor);
  });

  return Array.from(
    groups.entries()
  ).sort(
    (a, b) =>
      (ACTIVITY_CATEGORY_ORDER.indexOf(a[0]) +
        1 || 99) -
      (ACTIVITY_CATEGORY_ORDER.indexOf(b[0]) +
        1 || 99)
  );
}

function groupVenueResults(venues) {
  const groups = new Map();

  venues.forEach((venue) => {
    const key =
      venue.category ||
      'Other venues';

    if (!groups.has(key)) {
      groups.set(key, []);
    }

    groups.get(key).push(venue);
  });

  return Array.from(
    groups.entries()
  );
}

function takeDiversified(
  options,
  groupFor,
  limit = RECOMMENDATIONS_PER_SECTION
) {
  const selected = [];
  const selectedIds = new Set();
  const selectedGroups = new Set();

  options.forEach((option) => {
    if (selected.length >= limit) {
      return;
    }

    const group = groupFor(option);

    if (selectedGroups.has(group)) {
      return;
    }

    selected.push(option);
    selectedIds.add(option.id);
    selectedGroups.add(group);
  });

  options.forEach((option) => {
    if (
      selected.length >= limit ||
      selectedIds.has(option.id)
    ) {
      return;
    }

    selected.push(option);
    selectedIds.add(option.id);
  });

  return selected;
}

function formatActivityPrice(rate) {
  if (
    !rate ||
    rate.low == null ||
    rate.high == null
  ) {
    return 'Planning range unavailable';
  }

  const amount = `${rate.startingAt ? 'From ' : ''}${formatRange(
    rate.low,
    rate.high
  )}`;

  if (rate.basis === 'per_person') {
    return `${amount} per person`;
  }

  if (rate.basis === 'per_unit') {
    return `${amount} per ${
      rate.unitLabel || 'unit'
    }`;
  }

  return `${amount} flat`;
}

function activityCostRange(
  rate,
  headcount
) {
  if (
    !rate ||
    rate.low == null ||
    rate.high == null
  ) {
    return null;
  }

  const units =
    rate.basis === 'per_person'
      ? headcount
      : rate.basis === 'per_unit'
        ? Math.max(
            1,
            Math.ceil(
              headcount /
                (rate.unitCapacity || 1)
            )
          )
        : 1;

  return {
    low: Math.max(
      rate.low * units,
      rate.minimum || 0
    ),
    high: Math.max(
      rate.high * units,
      rate.minimum || 0
    ),
  };
}

function venueCostRange(
  venue,
  headcount
) {
  if (!venue) {
    return null;
  }

  const units =
    venue.priceBasis ===
    'per_person'
      ? headcount
      : 1;

  return {
    low: venue.priceLow * units,
    high: venue.priceHigh * units,
  };
}

const BUDGET_FIT_PREFIXES = {
  within: 'Within target',
  overlap: 'May exceed',
  over: 'Above target',
};

function budgetImpactText(
  budgetFit,
  costRange,
  headcount
) {
  if (
    !costRange ||
    !headcount
  ) {
    return BUDGET_FIT_PREFIXES[
      budgetFit
    ];
  }

  const perPerson = formatRange(
    costRange.low / headcount,
    costRange.high / headcount
  );

  return (
    BUDGET_FIT_PREFIXES[
      budgetFit
    ] +
    ' · adds about ' +
    perPerson +
    '/person'
  );
}

function formatFoodOptionPrice(option) {
  const amount = formatRange(
    option.low,
    option.high,
    true
  );

  if (option.basis === 'per_person') {
    return `${amount} per person`;
  }

  if (option.basis === 'per_item') {
    return `${amount} per ${
      option.unitLabel || 'item'
    }`;
  }

  if (option.basis === 'per_unit') {
    const unit =
      option.unitLabel || 'unit';
    const capacity =
      option.unitCapacity;

    return (
      `${amount} per ${unit}` +
      (capacity
        ? ` · serves up to ${capacity}`
        : '')
    );
  }

  if (
    option.basis ===
    'per_staff_hour'
  ) {
    return `${amount} per staff hour`;
  }

  return `${amount} per event`;
}

function formatFoodGroupSize(option) {
  const minimum =
    option.minHeadcount || 1;
  const maximum =
    option.maxHeadcount;

  return maximum
    ? `${minimum}–${maximum} people`
    : `${minimum}+ people`;
}

const BUDGET_FIT_ORDER = {
  within: 0,
  overlap: 1,
  over: 2,
};

function budgetFitForQuote(
  quote,
  targetTotal
) {
  if (
    quote.totals.high <=
    targetTotal
  ) {
    return 'within';
  }

  if (
    quote.totals.low <=
    targetTotal
  ) {
    return 'overlap';
  }

  return 'over';
}

function sortByBudgetFit(
  options,
  budgetFits
) {
  return options
    .map((option, index) => ({
      option,
      index,
      rank:
        BUDGET_FIT_ORDER[
          budgetFits.get(option.id)
        ] ?? 3,
    }))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        a.index - b.index
    )
    .map(({ option }) => option);
}

// Shares the running plan total with every budget badge, so labels reflect
// the whole package once something is selected.
const PlanTotalsContext = React.createContext(null);

function BudgetImpactBadge({
  budgetFit,
  costRange,
  headcount,
  planKey,
  isFood = false,
}) {
  const planTotals = React.useContext(PlanTotalsContext);
  const planTotal =
    planTotals && planTotals.showPlanTotal && planKey
      ? planTotals.totals.get(planKey)
      : null;
  const foodCovered =
    isFood && planTotals && planTotals.foodIncludedBy;

  let text;
  if (planTotal && headcount) {
    text = `${BUDGET_FIT_PREFIXES[budgetFit]} · plan total about ${formatRange(
      planTotal.low / headcount,
      planTotal.high / headcount
    )}/person`;
  } else {
    text = budgetImpactText(budgetFit, costRange, headcount);
  }

  let note = '';
  if (foodCovered) {
    note = `Not added to total · ${planTotals.foodIncludedBy} includes food`;
  } else if (isFood && planTotals) {
    note = planTotals.foodSurchargeNote;
  }

  return (
    <span
      className={[
        'qb-budget-fit',
        'qb-budget-fit-' +
          budgetFit,
      ].join(' ')}
    >
      {text}
      {note && (
        <small className="qb-budget-fit-note">
          {note}
        </small>
      )}
    </span>
  );
}

function RecommendationCard({
  category,
  title,
  price,
  priceSource,
  budgetFit,
  costRange,
  headcount,
  planKey,
  isFood,
  selected,
  selectionType,
  onSelect,
}) {
  const isVenue = selectionType === 'venue';
  const selectionLabel = isVenue
    ? selected
      ? 'Selected for comparison'
      : 'Select venue'
    : selected
      ? 'Added to package'
      : selectionType === 'food'
        ? 'Select food & drinks'
        : 'Select experience';

  const selectionAriaLabel = isVenue
    ? `Compare ${title}`
    : `${selected ? 'Remove' : 'Add'} ${title} ${selected ? 'from' : 'to'} package`;

  return (
    <li
      className={
        'qb-recommendation-card' +
        (selected ? ' is-selected' : '')
      }
    >
      <span className="qb-recommendation-category">
        {category}
      </span>

      <strong>{title}</strong>

      <span className="qb-recommendation-price">
        {price}
        <PriceSourceTag source={priceSource} />
      </span>

      <BudgetImpactBadge
        budgetFit={budgetFit}
        costRange={costRange}
        headcount={headcount}
        planKey={planKey}
        isFood={isFood}
      />

      <label className="qb-recommendation-selection">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          aria-label={selectionAriaLabel}
        />
        <span>{selectionLabel}</span>
      </label>
    </li>
  );
}

function quoteToText(
  quote,
  {
    dateText,
    perPersonBudget,
  }
) {
  const lines = [
    'Teamward Denver team-event estimate',

    `${
      quote.input.headcount
    } people | Denver, CO | ${
      formatMoney(
        perPersonBudget
      )
    }/person target${
      dateText
        ? ` | ${dateText}`
        : ''
    }${
      quote.input.weekend
        ? ' | weekend/peak rates'
        : ''
    }`,

    '',
  ];

  quote.lines.forEach((line) => {
    const amount = line.includedElsewhere
      ? 'Not added (food already included)'
      : formatRange(
          line.low,
          line.high
        );

    lines.push(
      `${line.label} - ${
        line.startingAt
          ? `Starting at ${amount}`
          : amount
      }${
        line.detail
          ? ` (${line.detail})`
          : ''
      }`
    );
  });

  lines.push('');

  lines.push(
    `Estimated total: ${formatRange(
      quote.totals.low,
      quote.totals.high
    )}`
  );

  lines.push(
    `Per person: ${formatRange(
      quote.totals.perPersonLow,
      quote.totals.perPersonHigh
    )}`
  );

  if (
    quote.quotedSeparately.length
  ) {
    lines.push('');

    lines.push(
      `Quoted separately: ${quote.quotedSeparately.join(
        ', '
      )}`
    );
  }

  lines.push('', ...(quote.warnings || []), '');

  lines.push(
    'Estimate only. Teamward confirms final pricing and availability with each ' +
      'venue before anything is booked.'
  );

  return lines.join('\n');
}

const QuoteBuilderPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const initial = useMemo(() => {
    const fromUrl = decodeQuote(
      location.search
    );

    // Generic builder links should always open a clean package. Previously,
    // an old locally saved quote could silently restore a venue (or other
    // selections), which made a fresh builder visit look preconfigured.
    // Saved state is now restored only when explicitly requested with
    // ?resume=1. URLs that intentionally preselect an activity/package keep
    // working because their query values still take precedence below.
    const searchParams = new URLSearchParams(
      location.search || ''
    );
    const shouldResumeSavedQuote =
      searchParams.get('resume') === '1';
    const saved = shouldResumeSavedQuote
      ? loadQuote()
      : null;

    const pick = (
      key,
      fallback
    ) => {
      if (fromUrl[key] != null) {
        return fromUrl[key];
      }

      if (
        saved &&
        saved[key] != null
      ) {
        return saved[key];
      }

      return fallback;
    };

    const requestedHeadcount =
      Number(
        pick('headcount', 25)
      );

    const requestedPerPersonBudget =
      Number(
        pick(
          'perPersonBudget',
          DEFAULT_PER_PERSON_BUDGET
        )
      );

    const requestedFoodIds =
      pick('foodIds', null);
    const legacyFoodId =
      pick('foodId', null);
    const initialFoodIds =
      Array.isArray(requestedFoodIds)
        ? requestedFoodIds
        : legacyFoodId &&
            legacyFoodId !== 'none'
          ? [legacyFoodId]
          : [];

    return {
      headcount: Math.min(
        HEADCOUNT_MAX,
        Math.max(
          HEADCOUNT_MIN,
          requestedHeadcount || 25
        )
      ),

      perPersonBudget: Math.min(
        PER_PERSON_BUDGET_MAX,
        Math.max(
          PER_PERSON_BUDGET_MIN,
          requestedPerPersonBudget ||
            DEFAULT_PER_PERSON_BUDGET
        )
      ),

      weekend: Boolean(
        pick('weekend', false)
      ),

      venueId: pick('venueId', null),
      venueIds: pick('venueIds', null) || (pick('venueId', null) ? [pick('venueId', null)] : []),

      vendorIds:
        pick('vendorIds', []) || [],

      foodIds: initialFoodIds,

      facilitationId:
        pick(
          'facilitationId',
          'none'
        ) || 'none',

      planningSupportId:
        pick(
          'planningSupportId',
          'none'
        ) || 'none',

      includeServiceCharge:
        Boolean(
          pick(
            'includeServiceCharge',
            true
          )
        ),

      dateText: String(
        pick('dateText', '') || ''
      ).slice(0, 80),
    };

    // Read only once when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    headcount,
    setHeadcount,
  ] = useState(
    initial.headcount
  );

  const [
    headcountText,
    setHeadcountText,
  ] = useState(
    String(initial.headcount)
  );

  const [
    perPersonBudget,
    setPerPersonBudget,
  ] = useState(
    initial.perPersonBudget
  );

  const [
    perPersonBudgetText,
    setPerPersonBudgetText,
  ] = useState(
    String(
      initial.perPersonBudget
    )
  );

  const [
    weekend,
    setWeekend,
  ] = useState(
    initial.weekend
  );

  const [venueIds, setVenueIds] = useState(initial.venueIds);
  const venueId = venueIds[0] || null;
  const setVenueId = (id) => setVenueIds(id ? [id] : []);

  const [
    vendorIds,
    setVendorIds,
  ] = useState(
    initial.vendorIds
  );

  const [
    foodIds,
    setFoodIds,
  ] = useState(
    initial.foodIds
  );

  const [
    facilitationId,
    setFacilitationId,
  ] = useState(
    initial.facilitationId
  );

  const [
    planningSupportId,
    setPlanningSupportId,
  ] = useState(
    initial.planningSupportId
  );

  const [
    includeServiceCharge,
    setIncludeServiceCharge,
  ] = useState(
    initial.includeServiceCharge
  );

  const [
    dateText,
    setDateText,
  ] = useState(
    initial.dateText
  );

  const [
    venueQuery,
    setVenueQuery,
  ] = useState('');

  const [
    venueArea,
    setVenueArea,
  ] = useState('all');

  const [
    outdoorOnly,
    setOutdoorOnly,
  ] = useState(false);

  const [
    venueBudgetOnly,
    setVenueBudgetOnly,
  ] = useState(false);

  const [
    activityBudgetOnly,
    setActivityBudgetOnly,
  ] = useState(false);

  const [
    openVenueCategory,
    setOpenVenueCategory,
  ] = useState(null);

  const [
    openActivityCategories,
    setOpenActivityCategories,
  ] = useState(
    () => new Set()
  );

  const [
    openFoodCategories,
    setOpenFoodCategories,
  ] = useState(
    () => new Set()
  );

  const [
    visibleVenueCount,
    setVisibleVenueCount,
  ] = useState(
    VENUES_PER_PAGE
  );

  const [
    quoteAction,
    setQuoteAction,
  ] = useState('');

  const [
    hostDetailsOpen,
    setHostDetailsOpen,
  ] = useState(false);

  const [
    activeStep,
    setActiveStep,
  ] = useState(0);

  const [
    visitedSteps,
    setVisitedSteps,
  ] = useState(
    () => new Set([0])
  );

  const [
    venueNotice,
    setVenueNotice,
  ] = useState('');

  const [
    summaryInView,
    setSummaryInView,
  ] = useState(false);

  // Basics only shows as done after the visitor confirms or changes them,
  // not just because defaults are prefilled.
  const [basicsConfirmed, setBasicsConfirmed] = useState(false);
  const confirmBasics = useCallback(() => setBasicsConfirmed(true), []);

  const [showAllFood, setShowAllFood] = useState(false);
  const [activitySort, setActivitySort] = useState('popular');
  const [foodSort, setFoodSort] = useState('popular');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [upsellDismissed, setUpsellDismissed] = useState(false);
  const [lead, setLead] = useState({
    name: '',
    email: '',
    company: '',
    date: '',
  });
  const [leadErrors, setLeadErrors] = useState({});
  const [leadStatus, setLeadStatus] = useState('');
  const leadSectionRef = useRef(null);
  const leadNameRef = useRef(null);
  const sheetCloseRef = useRef(null);

  const stepRefs = useRef([]);
  const summaryRef = useRef(null);
  const hasNavigatedSteps =
    useRef(false);
  const previousVenueId = useRef(
    initial.venueId
  );

  const goToStep = useCallback(
    (index) => {
      hasNavigatedSteps.current = true;
      if (index > 0) {
        setBasicsConfirmed(true);
      }
      setActiveStep(index);
      setVisitedSteps((previous) => {
        if (previous.has(index)) {
          return previous;
        }

        const next = new Set(previous);
        next.add(index);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!hasNavigatedSteps.current) {
      return;
    }

    const element =
      stepRefs.current[activeStep];

    if (
      element &&
      typeof element.scrollIntoView ===
        'function'
    ) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [activeStep]);

  const scrollToSummary =
    useCallback(() => {
      const element =
        summaryRef.current;

      if (
        element &&
        typeof element.scrollIntoView ===
          'function'
      ) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, []
  );

  useEffect(() => {
    const element =
      summaryRef.current;

    if (
      !element ||
      typeof window ===
        'undefined' ||
      typeof window.IntersectionObserver !==
        'function'
    ) {
      return undefined;
    }

    const observer =
      new window.IntersectionObserver(
        ([entry]) =>
          setSummaryInView(
            entry.isIntersecting
          ),
        { threshold: 0.15 }
      );

    observer.observe(element);

    return () =>
      observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, []);

  useEffect(() => {
    const previousTitle =
      document.title;

    document.title =
      'Denver Team Event Budget Builder | Teamward';

    return () => {
      document.title =
        previousTitle;
    };
  }, []);

  useEffect(() => {
    setHeadcountText(
      String(headcount)
    );
  }, [headcount]);

  useEffect(() => {
    setPerPersonBudgetText(
      String(perPersonBudget)
    );
  }, [perPersonBudget]);

  useEffect(() => {
    if (!sheetOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSheetOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    if (sheetCloseRef.current) sheetCloseRef.current.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sheetOpen]);

  const state = {
    headcount,
    perPersonBudget,
    weekend,
    venueId,
    venueIds,
    vendorIds,
    foodIds,
    facilitationId,
    planningSupportId,
    includeServiceCharge,
    dateText,
  };

  useEffect(() => {
    saveQuote(state);

    const query =
      encodeQuote(state);

    navigate(
      {
        pathname:
          location.pathname,

        search: query
          ? `?${query}`
          : '',
      },
      {
        replace: true,
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    headcount,
    perPersonBudget,
    weekend,
    venueId,
    venueIds,
    vendorIds,
    foodIds,
    facilitationId,
    planningSupportId,
    includeServiceCharge,
    dateText,
  ]);

  const available = useMemo(
    () =>
      eligibleVendors(
        experiences.filter(
          (vendor) => !QUOTE_BUILDER_EXCLUDED_VENDOR_IDS.has(vendor.id)
        ),
        {
          city: CITY,
          headcount,
        }
      ),
    [headcount]
  );

  const validVendorIds =
    useMemo(
      () =>
        new Set(
          available.map(
            (vendor) =>
              vendor.id
          )
        ),
      [available]
    );

  const selectedVendors =
    useMemo(
      () =>
        vendorIds
          .filter((id) =>
            validVendorIds.has(id)
          )
          .map((id) =>
            available.find(
              (vendor) =>
                vendor.id === id
            )
          )
          .filter(Boolean),
      [
        available,
        validVendorIds,
        vendorIds,
      ]
    );

  const selectedVendorIds =
    useMemo(
      () =>
        new Set(
          selectedVendors.map(
            (vendor) =>
              vendor.id
          )
        ),
      [selectedVendors]
    );

  const selectedFoodIds =
    useMemo(
      () =>
        new Set(
          foodIds.filter((id) =>
            FOOD_OPTIONS_BY_ID.has(id)
          )
        ),
      [foodIds]
    );

  const selectedVenues = useMemo(
    () => venueIds.map((id) => QUOTE_VENUES.find((venue) => venue.id === id)).filter(Boolean),
    [venueIds]
  );
  const selectedVenue = selectedVenues[0] || null;
  const officeHosted = venueIds.includes('your-office');

  useEffect(() => {
    // Re-collapse alternatives whenever the location mode changes.
    setOpenActivityCategories(new Set());
    setOpenFoodCategories(new Set());
    setOpenVenueCategory(null);
  }, [officeHosted]);

  const selectedFacilitation =
    useMemo(
      () =>
        FACILITATION_OPTIONS.find(
          (option) =>
            option.id ===
            facilitationId
        ) ||
        FACILITATION_OPTIONS[0],
      [facilitationId]
    );

  const selectedPlanningSupport =
    useMemo(
      () =>
        PLANNING_SUPPORT_OPTIONS.find(
          (option) =>
            option.id ===
            planningSupportId
        ) || null,
      [planningSupportId]
    );

  const quote = useMemo(
    () =>
      buildQuote({
        headcount,
        weekend,
        venues: selectedVenues,
        vendors:
          selectedVendors,
        foodOptions:
          FOOD_OPTIONS.filter(
            (option) =>
              selectedFoodIds.has(
                option.id
              )
          ),
        facilitation:
          selectedFacilitation,
        planningSupport:
          selectedPlanningSupport,
        includeServiceCharge,
      }),
    [
      facilitationId,
      foodIds,
      headcount,
      includeServiceCharge,
      planningSupportId,
      selectedFacilitation,
      selectedFoodIds,
      selectedPlanningSupport,
      selectedVenues,
      selectedVendors,
      vendorIds,
      venueId,
      weekend,
    ]
  );

  const targetTotal =
    headcount * perPersonBudget;

  const quoteLow =
    quote.totals.low;
  const quoteHigh =
    quote.totals.high;

  const budgetFits =
    useMemo(() => {
      const venueFits = new Map();
      const activityFits = new Map();
      const foodFits = new Map();
      const planTotals = new Map();

      QUOTE_VENUES.forEach(
        (venue) => {
          const venueQuote =
            buildQuote({
              headcount,
              weekend,
              venue,
              vendors:
                selectedVendors,
              foodOptions:
                FOOD_OPTIONS.filter(
                  (option) =>
                    selectedFoodIds.has(
                      option.id
                    )
                ),
              facilitation:
                selectedFacilitation,
              planningSupport:
                selectedPlanningSupport,
              includeServiceCharge,
            });

          planTotals.set(`venue:${venue.id}`, venueQuote.totals);
          venueFits.set(
            venue.id,
            budgetFitForQuote(
              venueQuote,
              targetTotal
            )
          );
        }
      );

      available.forEach(
        (vendor) => {
          const vendorQuote =
            buildQuote({
              headcount,
              weekend,
              venues: selectedVenues,
              vendors: [
                ...selectedVendors.filter(
                  (selected) =>
                    selected.id !==
                    vendor.id
                ),
                vendor,
              ],
              foodOptions:
                FOOD_OPTIONS.filter(
                  (option) =>
                    selectedFoodIds.has(
                      option.id
                    )
                ),
              facilitation:
                selectedFacilitation,
              planningSupport:
                selectedPlanningSupport,
              includeServiceCharge,
            });

          planTotals.set(`activity:${vendor.id}`, vendorQuote.totals);
          activityFits.set(
            vendor.id,
            budgetFitForQuote(
              vendorQuote,
              targetTotal
            )
          );
        }
      );

      FOOD_OPTIONS.forEach(
        (food) => {
          const foodQuote =
            buildQuote({
              headcount,
              weekend,
              venues: selectedVenues,
              vendors:
                selectedVendors,
              foodOptions: [
                ...FOOD_OPTIONS.filter(
                  (option) =>
                    selectedFoodIds.has(
                      option.id
                    ) &&
                    option.id !==
                    food.id
                ),
                food,
              ],
              facilitation:
                selectedFacilitation,
              planningSupport:
                selectedPlanningSupport,
              includeServiceCharge,
            });

          planTotals.set(`food:${food.id}`, foodQuote.totals);
          foodFits.set(
            food.id,
            budgetFitForQuote(
              foodQuote,
              targetTotal
            )
          );
        }
      );

      return {
        venue: venueFits,
        activity: activityFits,
        food: foodFits,
        totals: planTotals,
      };
    }, [
      available,
      headcount,
      includeServiceCharge,
      selectedFacilitation,
      selectedFoodIds,
      selectedPlanningSupport,
      selectedVenues,
      selectedVendors,
      targetTotal,
      weekend,
    ]
  );

  const recommendedVenues =
    useMemo(() => {
      const options =
        QUOTE_VENUES.filter(
          (venue) =>
            venue.id !==
              ACTIVITY_LOCATION_VENUE_ID &&
            venue.capacity >=
              headcount &&
            (!officeHosted || venue.id === 'your-office') &&
            // Recommendations only show options that still fit the target;
            // over-target venues remain available in the full list below.
            (budgetFits.venue.get(venue.id) !== 'over' ||
              venueIds.includes(venue.id))
        );

      return takeDiversified(
        sortByBudgetFit(
          options,
          budgetFits.venue
        ),
        (venue) =>
          venue.category ||
          'Other venues'
      );
    }, [
      budgetFits.venue,
      headcount,
      officeHosted,
      venueIds,
    ]
  );

  const recommendedActivities =
    useMemo(() => {
      const nonDining =
        available.filter(
          (vendor) =>
            activityCategoryFor(
              vendor
            ) !==
            DINING_CATEGORY &&
            (!officeHosted || isOfficeActivity(vendor)) &&
            (budgetFits.activity.get(vendor.id) !== 'over' ||
              vendorIds.includes(vendor.id))
        );

      return takeDiversified(
        sortByBudgetFit(
          nonDining,
          budgetFits.activity
        ),
        activityCategoryFor
      );
    }, [
      available,
      budgetFits.activity,
      officeHosted,
      vendorIds,
    ]
  );

  const recommendedFood =
    useMemo(() => {
      const selected = [];
      const seen = new Set();

      FOOD_RECOMMENDATION_GROUPS.forEach(
        (group) => {
          const option =
            group.ids
              .map((id) =>
                FOOD_OPTIONS_BY_ID.get(
                  id
                )
              )
              .filter(Boolean)
              .filter((option) => !officeHosted || isOfficeFood(option))
              .filter(
                (candidate) =>
                  candidate.minHeadcount <=
                  headcount &&
                  (budgetFits.food.get(candidate.id) !== 'over' ||
                    foodIds.includes(candidate.id))
              )
              .sort(
                (a, b) =>
                  (BUDGET_FIT_ORDER[
                    budgetFits.food.get(
                      a.id
                    )
                  ] ?? 3) -
                  (BUDGET_FIT_ORDER[
                    budgetFits.food.get(
                      b.id
                    )
                  ] ?? 3)
              )[0];

          if (
            option &&
            !seen.has(option.id)
          ) {
            seen.add(option.id);
            selected.push(option);
          }
        }
      );

      if (
        selected.length <
        RECOMMENDATIONS_PER_SECTION
      ) {
        sortByBudgetFit(
          FOOD_OPTIONS.filter(
            (option) =>
              option.minHeadcount <=
              headcount && (!officeHosted || isOfficeFood(option)) &&
              (budgetFits.food.get(option.id) !== 'over' ||
                foodIds.includes(option.id))
          ),
          budgetFits.food
        ).forEach((option) => {
          if (
            selected.length >=
              RECOMMENDATIONS_PER_SECTION ||
            seen.has(option.id)
          ) {
            return;
          }

          seen.add(option.id);
          selected.push(option);
        });
      }

      return selected;
    }, [
      budgetFits.food,
      foodIds,
      headcount,
      officeHosted,
    ]
  );

  const venueResults =
    useMemo(
      () =>
        filterDenverVenues(
          QUOTE_VENUES.filter(
            (venue) =>
              venue.id !==
              ACTIVITY_LOCATION_VENUE_ID
          ),
          {
            headcount,
            area: venueArea,
            outdoorOnly,
            query: venueQuery,
          }
        ),
      [
        headcount,
        outdoorOnly,
        venueArea,
        venueQuery,
      ]
    );

  const visibleVenues =
    useMemo(
      () =>
        venueResults.slice(
          0,
          visibleVenueCount
        ),
      [
        venueResults,
        visibleVenueCount,
      ]
    );

  const groupedVisibleVenues =
    useMemo(
      () =>
        groupVenueResults(
          officeHosted ? venueResults : visibleVenues
        ),
      [officeHosted, venueResults, visibleVenues]
    );

  const activitiesByCategory =
    useMemo(() => {
      const costLow = (vendor) => {
        const range = activityCostRange(
          rateFor(vendor, { headcount, weekend }),
          headcount
        );
        return range ? range.low : null;
      };

      return groupByCategory(
        available.filter(
          (vendor) =>
            activityCategoryFor(
              vendor
            ) !==
            DINING_CATEGORY
        )
      ).map(([category, vendors]) => [
        category,
        sortOptions(vendors, activitySort, {
          fitBand: (vendor) => vendorSizeFitBand(vendor, headcount),
          budgetFit: (vendor) => budgetFits.activity.get(vendor.id),
          costLow,
        }),
      ]);
    }, [activitySort, available, budgetFits.activity, headcount, weekend]);

  const diningExperiences =
    useMemo(
      () =>
        available.filter(
          (vendor) =>
            activityCategoryFor(
              vendor
            ) ===
            DINING_CATEGORY
        ),
      [available]
    );

  const foodCostLow =
    useMemo(() => {
      const costs = new Map();
      FOOD_OPTIONS.forEach((option) => {
        const optionQuote = buildQuote({
          headcount,
          weekend,
          foodOptions: [option],
          includeServiceCharge: false,
        });
        costs.set(option.id, optionQuote.totals.low);
      });
      return costs;
    }, [headcount, weekend]);

  const foodCategories =
    useMemo(() => {
      const groups = new Map();

      FOOD_DISPLAY_ORDER.forEach(
        (category) => {
          groups.set(category, []);
        }
      );

      FOOD_OPTIONS.forEach(
        (option) => {
          const category =
            foodDisplayCategory(
              option.category
            );

          if (!groups.has(category)) {
            groups.set(
              category,
              []
            );
          }

          groups.get(category).push(
            option
          );
        }
      );

      return Array.from(
        groups.entries()
      )
        .filter(
          ([, options]) =>
            options.length > 0
        )
        .map(([category, options]) => [
          category,
          sortOptions(options, foodSort, {
            fitBand: (option) => foodSizeFitBand(option, headcount),
            budgetFit: (option) => budgetFits.food.get(option.id),
            costLow: (option) => foodCostLow.get(option.id),
            boost: (option) => FOOD_FAVORITE_IDS.has(option.id),
          }),
        ]);
    }, [budgetFits.food, foodCostLow, foodSort, headcount]
  );

  const foodOptionCount = foodCategories.reduce(
    (sum, [, options]) => sum + options.length,
    0
  );

  const starterFits =
    useMemo(() => {
      const fits = new Map();
      STARTER_PACKAGES.forEach((starter) => {
        const vendors = (starter.vendorIds || [])
          .map((id) => available.find((vendor) => vendor.id === id))
          .filter(Boolean);
        const venue = starter.venueId
          ? QUOTE_VENUES.find((item) => item.id === starter.venueId)
          : null;
        const starterQuote = buildQuote({
          headcount,
          weekend,
          venue,
          vendors,
          foodOptions: FOOD_OPTIONS.filter((option) =>
            (starter.foodIds || []).includes(option.id)
          ),
          includeServiceCharge: true,
        });
        // When a starter's range is wide, name the line that drives it.
        const { perPersonLow, perPersonHigh } = starterQuote.totals;
        const widest = starterQuote.lines
          .filter((line) => Number.isFinite(line.low) && Number.isFinite(line.high))
          .sort((a, b) => (b.high - b.low) - (a.high - a.low))[0];
        const rangeDriver =
          widest && perPersonLow > 0 && perPersonHigh / perPersonLow >= 2
            ? widest
            : null;
        fits.set(starter.id, {
          fit: budgetFitForQuote(starterQuote, targetTotal),
          perPersonLow,
          perPersonHigh,
          overBy: Math.max(0, starterQuote.totals.low - targetTotal),
          rangeDriver,
        });
      });
      return fits;
    }, [available, headcount, targetTotal, weekend]);

  const fittingStarters = STARTER_PACKAGES.filter(
    (starter) => {
      const fit = starterFits.get(starter.id);
      return fit && fit.fit === 'within';
    }
  );

  const packageVendorCount = countPackageVendors({
    venueIds,
    vendorIds: selectedVendors.map((vendor) => vendor.id),
    foodOptions: FOOD_OPTIONS.filter((option) =>
      selectedFoodIds.has(option.id)
    ),
  });

  const showCoordinationOffer =
    Boolean(VENDOR_COORDINATION_OPTION) &&
    packageVendorCount >= 2 &&
    !upsellDismissed &&
    (planningSupportId === 'none' || planningSupportId === 'self');

  // A $0 "I'll coordinate it myself" choice alone is not a package yet.
  const hasPackageItems = quote.lines.some(
    (line) => !(line.kind === 'planningSupport' && !line.high)
  );
  const overTargetBy = Math.max(0, quote.totals.low - targetTotal);

  const venueGroups =
    useMemo(
      () =>
        groupVenueResults(
          venueResults
        ),
      [venueResults]
    );

  const selectedActivityHasOwnVenue =
    selectedVendors.some(
      hasOwnVenue
    );

  useEffect(() => {
    setVenueIds((previous) => {
      if (selectedActivityHasOwnVenue && !previous.length) {
        return [ACTIVITY_LOCATION_VENUE_ID];
      }
      if (selectedVendors.length && !selectedActivityHasOwnVenue &&
        previous.includes(ACTIVITY_LOCATION_VENUE_ID)) {
        return previous.filter((id) => id !== ACTIVITY_LOCATION_VENUE_ID);
      }
      return previous;
    });
  }, [selectedActivityHasOwnVenue, selectedVendors.length]);

  useEffect(() => {
    if (
      previousVenueId.current ===
      venueId
    ) {
      return;
    }

    if (
      venueId ===
      ACTIVITY_LOCATION_VENUE_ID
    ) {
      setVenueNotice(
        'Venue updated: this experience uses its own location.'
      );
    } else if (
      previousVenueId.current ===
      ACTIVITY_LOCATION_VENUE_ID &&
      !venueId
    ) {
      setVenueNotice(
        'Venue cleared: choose a venue for this experience.'
      );
    } else {
      setVenueNotice('');
    }

    previousVenueId.current =
      venueId;
  }, [venueId]);

  const applyStarterPackage =
    (starter) => {
      setVenueId(
        starter.venueId || null
      );
      setVendorIds(
        starter.vendorIds || []
      );
      setFoodIds(
        starter.foodIds || []
      );
      setFacilitationId('none');
      setPlanningSupportId('none');
      setIncludeServiceCharge(true);
      setVenueNotice('');
      setBasicsConfirmed(true);
      goToStep(0);
    };

  const toggleVendor = (id) => {
    setVendorIds((previous) =>
      previous.includes(id)
        ? previous.filter(
            (item) => item !== id
          )
        : [...previous, id]
    );
  };

  const toggleFood = (id) => {
    setFoodIds((previous) =>
      previous.includes(id)
        ? previous.filter(
            (item) => item !== id
          )
        : [...previous, id]
    );
  };

  const setVenue = (id) => {
    setVenueIds((previous) => previous.includes(id)
      ? previous.filter((item) => item !== id)
      : [...previous, id]);
    setVenueNotice('');
  };

  const goToLeadForm = () => {
    setSheetOpen(false);
    const section = leadSectionRef.current;
    if (section && typeof section.scrollIntoView === 'function') {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    window.setTimeout(() => {
      if (leadNameRef.current) {
        leadNameRef.current.focus({ preventScroll: true });
      }
    }, 350);
  };

  const updateLead = (field) => (event) => {
    const { value } = event.target;
    setLead((previous) => ({ ...previous, [field]: value }));
    setLeadErrors((previous) =>
      previous[field] ? { ...previous, [field]: '' } : previous
    );
    setLeadStatus('');
  };

  const submitLead = (event) => {
    event.preventDefault();

    if (!hasPackageItems) {
      return;
    }

    const errors = {};
    if (!lead.name.trim()) errors.name = 'Enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) {
      errors.email = 'Enter a valid work email.';
    }
    if (!lead.company.trim()) errors.company = 'Enter your company name.';
    if (!lead.date) errors.date = 'Choose an event date.';

    setLeadErrors(errors);
    if (Object.keys(errors).length) {
      setLeadStatus('');
      return;
    }

    const pickedNames = [
      ...selectedVenues
        .filter((venue) => venue.id !== ACTIVITY_LOCATION_VENUE_ID)
        .map((venue) => venue.name),
      ...selectedVendors.map((vendor) => vendor.name),
    ];

    const details = [
      quoteToText(quote, { dateText: dateText || lead.date, perPersonBudget }),
      '',
      `Saved plan: ${window.location.href}`,
    ].join('\n');

    const url = planRequestUrl({
      name: lead.name,
      email: lead.email,
      company: lead.company,
      date: lead.date,
      headcount,
      city: CITY,
      state: STATE,
      summary: [
        pickedNames.length ? pickedNames.join(' + ') : 'Denver team-event package',
        hasPackageItems
          ? `Estimated ${formatRange(quote.totals.low, quote.totals.high)}`
          : null,
        selectedPlanningSupport
          ? `Support: ${selectedPlanningSupport.label}`
          : null,
      ]
        .filter(Boolean)
        .join(' | '),
      budgetText: `${formatMoney(perPersonBudget)}/person target (${formatMoney(targetTotal)} total)`,
      details,
    });

    // Open in a new tab so the builder stays put; fall back to this tab when
    // pop-ups are blocked. ("noopener" would make window.open return null.)
    const opened = window.open(url, '_blank');
    if (!opened) {
      window.location.href = url;
      return;
    }
    opened.opener = null;
    setLeadStatus('opened');
  };

  const openPrintDialog = () => {
    const html = buildQuoteDocument(quote, {
      dateText,
      perPersonBudget,
      logoUrl: quoteDocumentLogo,
    });
    const preview = window.open('', '_blank');
    if (!preview) {
      setQuoteAction('Please allow pop-ups to view your event plan.');
      return;
    }
    preview.opener = null;
    preview.document.open();
    preview.document.write(html);
    preview.document.close();
  };

  const quoteUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : '';

  const startOver = () => {
    setBasicsConfirmed(false);
    setHeadcount(25);
    setPerPersonBudget(
      DEFAULT_PER_PERSON_BUDGET
    );
    setWeekend(false);
    setVenueId(null);
    setVendorIds([]);
    setFoodIds([]);
    setFacilitationId('none');
    setPlanningSupportId('none');
    setUpsellDismissed(false);
    setIncludeServiceCharge(true);
    setDateText('');
    setVenueQuery('');
    setVenueArea('all');
    setOutdoorOnly(false);
    setVenueBudgetOnly(false);
    setActivityBudgetOnly(false);
    setOpenVenueCategory(null);
    setOpenActivityCategories(
      new Set()
    );
    setOpenFoodCategories(
      new Set()
    );
    setVisibleVenueCount(
      VENUES_PER_PAGE
    );
    setVenueNotice('');
    setActiveStep(0);
    setVisitedSteps(new Set([0]));
    hasNavigatedSteps.current = false;
  };

  const renderSummary = (context) => (
    <>
      <div className="qb-summary-heading">
        <p className="qb-section-eyebrow">
          Your package
        </p>
        <h2>Estimated budget</h2>
        <p>
          {headcount} people | Denver, CO | {formatMoney(perPersonBudget)}/person target
        </p>
        {hasPackageItems && (
          <small>
            Uncheck an optional item to remove it.
          </small>
        )}
      </div>

      <div className="qb-summary-table">
        {hasPackageItems ? (
          <>
            <div className="qb-summary-table-head">
              <span>Item</span>
              <span>Cost</span>
            </div>

            {quote.lines.map((line) => (
              <div
                className="qb-summary-line"
                key={line.id}
              >
                <label className="qb-summary-line-label">
                  {line.optional ? (
                    <input
                      type="checkbox"
                      checked
                      onChange={() => {
                        if (line.type === 'vendor') {
                          toggleVendor(line.sourceId);
                        } else if (line.type === 'food') {
                          toggleFood(line.sourceId);
                        } else if (line.type === 'venue') {
                          setVenue(line.sourceId);
                        } else if (line.type === 'facilitation') {
                          setFacilitationId('none');
                        } else if (line.type === 'planning') {
                          setPlanningSupportId('none');
                        }
                      }}
                      aria-label={`Remove ${line.label}`}
                    />
                  ) : (
                    <span
                      className="qb-summary-check"
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                  )}

                  <span>
                    <strong>
                      {line.label}
                    </strong>
                    {line.detail && (
                      <small>
                        {line.detail}
                      </small>
                    )}
                  </span>
                </label>

                <span className="qb-summary-line-cost">
                  {line.includedElsewhere
                    ? 'Not added'
                    : line.startingAt
                      ? `Starting at ${formatRange(line.low, line.high)}`
                      : formatRange(line.low, line.high)}
                </span>
              </div>
            ))}

            <div className="qb-summary-total">
              <span>
                Estimated total
              </span>
              <strong>
                {formatRange(
                  quote.totals.low,
                  quote.totals.high
                )}
              </strong>
            </div>

            <div className="qb-summary-per-person">
              <span>
                Estimated per person
              </span>
              <strong>
                {formatRange(
                  quote.totals.perPersonLow,
                  quote.totals.perPersonHigh
                )}
              </strong>
            </div>

            {overTargetBy > 0 && (
              <p className="qb-summary-over" role="status">
                About {formatMoney(overTargetBy)} over your {formatMoney(targetTotal)} budget.
              </p>
            )}
          </>
        ) : (
          <div className="qb-summary-empty">
            <p>
              Pick a venue or experience to see your estimate
            </p>
          </div>
        )}
      </div>

      {selectedVendorIds.has('denver-brad-montgomery') && (
        <p className="qb-summary-keynote">
          <a
            href="https://bradmontgomery.teamward.ai/"
            target="_blank"
            rel="noopener noreferrer"
          >
            View keynote details
          </a>
          {' · Keynote fee only; travel and lodging are additional.'}
        </p>
      )}

      {quote.warnings.length > 0 && (
        <div className="qb-summary-notes" aria-label="Quote notes">
          <h3>Quote notes</h3>
          {quote.warnings.map((note) => <p key={note}>{note}</p>)}
        </div>
      )}

      {showCoordinationOffer && (
        <div className="qb-coordination-offer">
          <p className="qb-coordination-offer-title">
            Booking {packageVendorCount} vendors? Let Teamward coordinate them for {formatMoney(VENDOR_COORDINATION_OPTION.low)}.
          </p>
          <p>
            We confirm availability, collect final pricing, and handle communication with up to three vendors.
          </p>
          <div className="qb-coordination-offer-actions">
            <button
              type="button"
              className="qb-coordination-add"
              onClick={() => setPlanningSupportId(VENDOR_COORDINATION_OPTION.id)}
            >
              Add vendor coordination · {formatMoney(VENDOR_COORDINATION_OPTION.low)}
            </button>
            <button
              type="button"
              className="qb-link-action"
              onClick={() => setUpsellDismissed(true)}
            >
              No thanks
            </button>
          </div>
        </div>
      )}

      <div className="qb-summary-actions">
        <button
          type="button"
          className="qb-primary-action"
          onClick={goToLeadForm}
          disabled={!hasPackageItems}
          aria-describedby={!hasPackageItems ? `qb-send-hint-${context}` : undefined}
        >
          Send this plan to Teamward
        </button>

        {!hasPackageItems && (
          <p className="qb-send-hint" id={`qb-send-hint-${context}`}>
            Pick a venue or experience to see your estimate.
          </p>
        )}

        <button
          type="button"
          className="qb-secondary-action"
          onClick={openPrintDialog}
          disabled={!hasPackageItems}
        >
          View my event plan
        </button>

        {quoteAction && (
          <p role="status">{quoteAction}</p>
        )}

        <button
          type="button"
          className="qb-link-action"
          onClick={() => {
            if (context === 'sheet') setSheetOpen(false);
            startOver();
          }}
        >
          Start over
        </button>
      </div>

      <p className="qb-summary-note">
        Estimates are planning ranges. Availability, taxes, travel, service fees, and final vendor pricing may change the total.
      </p>
    </>
  );

  const foodTaxRate = TAX_RATES[CITY] ? TAX_RATES[CITY].foodAndDrink : 0;
  const foodSurchargeNote = [
    foodTaxRate ? `${Math.round(foodTaxRate * 100)}% tax` : '',
    includeServiceCharge
      ? `${Math.round(SERVICE_CHARGE_ALLOWANCE_RATE * 100)}% service & gratuity`
      : '',
  ].filter(Boolean).join(' + ');
  const planTotalsValue = {
    showPlanTotal: hasPackageItems,
    totals: budgetFits.totals,
    foodIncludedBy: quote.foodBundledBy || null,
    foodSurchargeNote: foodSurchargeNote
      ? `Includes est. ${foodSurchargeNote}`
      : '',
  };

  return (
    <PlanTotalsContext.Provider value={planTotalsValue}>
    <main className="quote-builder-page">
      <header className="qb-hero">
        <p className="qb-eyebrow">
          Create your own package | Denver
        </p>
        <h1>
          Build your team event. See what it could cost.
        </h1>
        <p className="qb-lede">
          Choose a venue, experience, food, and support. Your realistic planning range updates as you go.
        </p>
      </header>

      <section
        className="qb-progress"
        aria-label="Package builder progress"
      >
        <p className="qb-progress-title">
          Build your package
        </p>

        <div className="qb-progress-steps">
          {BUILDER_STEPS.map(
            (step, index) => {
              const complete =
                (step.key ===
                  'basics' &&
                  headcount > 0 &&
                  (basicsConfirmed || hasPackageItems)) ||
                (step.key ===
                  'venue' &&
                  Boolean(
                    selectedVenue
                  )) ||
                (step.key ===
                  'experience' &&
                  selectedVendors.length >
                    0) ||
                (step.key ===
                  'food' &&
                  selectedFoodIds.size >
                    0) ||
                (step.key ===
                  'planning' &&
                  (facilitationId !==
                    'none' ||
                    planningSupportId !==
                      'none'));
              const current =
                activeStep === index;
              const visited =
                visitedSteps.has(index);

              return (
                <React.Fragment
                  key={step.key}
                >
                  <button
                    type="button"
                    className={[
                      'qb-progress-step',
                      current
                        ? 'is-active'
                        : '',
                      complete
                        ? 'is-complete'
                        : '',
                      visited
                        ? 'is-visited'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-current={
                      current
                        ? 'step'
                        : undefined
                    }
                    onClick={() =>
                      goToStep(index)
                    }
                  >
                    <span className="qb-progress-number">
                      {complete
                        ? '✓'
                        : String(
                            index + 1
                          ).padStart(
                            2,
                            '0'
                          )}
                    </span>
                    <span>
                      {step.label}
                    </span>
                  </button>

                  {index <
                    BUILDER_STEPS.length -
                      1 && (
                    <span
                      className="qb-progress-arrow"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  )}
                </React.Fragment>
              );
            }
          )}
        </div>
      </section>

      <p className="qb-rate-note">
        Every price is tagged as a <strong>Published rate</strong> from the vendor or a <strong>Teamward estimate</strong>. Final pricing is confirmed before booking.
      </p>

      <div className="qb-builder-layout">
        <div className="qb-builder-form">
          <section
            className="qb-section"
            ref={(element) => {
              stepRefs.current[0] =
                element;
            }}
          >
            <div className="qb-section-heading">
              <span className="qb-section-number">
                01
              </span>
              <h2>The basics</h2>
            </div>

            <div className="qb-basics-stack" onChange={confirmBasics}>
              <div className="qb-basics-card qb-headcount-card">
                <div className="qb-field-heading">
                  <div>
                    <h3>
                      How many people?
                    </h3>
                    <p>
                      Use the slider or type an exact count.
                    </p>
                  </div>

                  <label
                    className="qb-number-field"
                    htmlFor="qb-headcount"
                  >
                    <input
                      id="qb-headcount"
                      type="number"
                      min={HEADCOUNT_MIN}
                      max={HEADCOUNT_MAX}
                      value={
                        headcountText
                      }
                      onChange={
                        (event) =>
                          setHeadcountText(
                            event.target.value
                          )
                      }
                      onBlur={() => {
                        const next =
                          Number(
                            headcountText
                          );
                        const normalized =
                          Math.min(
                            HEADCOUNT_MAX,
                            Math.max(
                              HEADCOUNT_MIN,
                              Number.isFinite(
                                next
                              )
                                ? next
                                : headcount
                            )
                          );
                        setHeadcount(
                          normalized
                        );
                        setHeadcountText(
                          String(
                            normalized
                          )
                        );
                      }}
                      onKeyDown={
                        (event) => {
                          if (
                            event.key ===
                            'Enter'
                          ) {
                            event.currentTarget.blur();
                          }
                        }
                      }
                    />
                    <span>people</span>
                  </label>
                </div>

                <input
                  className="qb-range"
                  type="range"
                  min={HEADCOUNT_MIN}
                  max={HEADCOUNT_MAX}
                  value={headcount}
                  onChange={
                    (event) =>
                      setHeadcount(
                        Number(
                          event.target.value
                        )
                      )
                  }
                  aria-label="Headcount"
                  style={{
                    '--range-progress': `${
                      ((headcount -
                        HEADCOUNT_MIN) /
                        (HEADCOUNT_MAX -
                          HEADCOUNT_MIN)) *
                      100
                    }%`,
                  }}
                />

                <div className="qb-range-labels">
                  <span>
                    {HEADCOUNT_MIN} people
                  </span>
                  <span>
                    {HEADCOUNT_MAX}+ people
                  </span>
                </div>
              </div>

              <div className="qb-basics-card qb-budget-card">
                <div className="qb-field-heading">
                  <div>
                    <h3>
                      What are you aiming to spend?
                    </h3>
                    <p>
                      Set a planning target. We’ll show what fits comfortably, what may stretch it, and what exceeds it.
                    </p>
                  </div>
                </div>

                <div className="qb-budget-controls">
                  <label
                    className="qb-number-field qb-budget-number-field"
                    htmlFor="qb-per-person-budget"
                  >
                    <span className="qb-budget-prefix">$</span>
                    <input
                      id="qb-per-person-budget"
                      type="number"
                      min={PER_PERSON_BUDGET_MIN}
                      max={PER_PERSON_BUDGET_MAX}
                      step={PER_PERSON_BUDGET_STEP}
                      value={
                        perPersonBudgetText
                      }
                      onChange={
                        (event) =>
                          setPerPersonBudgetText(
                            event.target.value
                          )
                      }
                      onBlur={() => {
                        const next =
                          Number(
                            perPersonBudgetText
                          );
                        const normalized =
                          Math.min(
                            PER_PERSON_BUDGET_MAX,
                            Math.max(
                              PER_PERSON_BUDGET_MIN,
                              Number.isFinite(
                                next
                              )
                                ? next
                                : perPersonBudget
                            )
                          );
                        setPerPersonBudget(
                          normalized
                        );
                        setPerPersonBudgetText(
                          String(
                            normalized
                          )
                        );
                      }}
                      onKeyDown={
                        (event) => {
                          if (
                            event.key ===
                            'Enter'
                          ) {
                            event.currentTarget.blur();
                          }
                        }
                      }
                    />
                    <span>/person</span>
                  </label>

                  <div className="qb-budget-summary">
                    <span>Overall budget</span>
                    <strong>
                      {formatMoney(
                        targetTotal
                      )}
                    </strong>
                  </div>
                </div>

                <input
                  className="qb-range qb-budget-range"
                  type="range"
                  min={PER_PERSON_BUDGET_MIN}
                  max={PER_PERSON_BUDGET_MAX}
                  step={PER_PERSON_BUDGET_STEP}
                  value={perPersonBudget}
                  onChange={
                    (event) =>
                      setPerPersonBudget(
                        Number(
                          event.target.value
                        )
                      )
                  }
                  aria-label="Per-person planning target"
                  style={{
                    '--range-progress': `${
                      ((perPersonBudget -
                        PER_PERSON_BUDGET_MIN) /
                        (PER_PERSON_BUDGET_MAX -
                          PER_PERSON_BUDGET_MIN)) *
                      100
                    }%`,
                  }}
                />

                <div className="qb-range-labels">
                  <span>
                    ${PER_PERSON_BUDGET_MIN}/person
                  </span>
                  <span>
                    ${PER_PERSON_BUDGET_MAX}+/person
                  </span>
                </div>
              </div>

              <div className="qb-basics-card qb-details-card">
                <div className="qb-field-heading">
                  <div>
                    <h3>
                      A few planning details
                    </h3>
                    <p>
                      These are optional. Add them if they help narrow your estimate.
                    </p>
                  </div>
                </div>

                <div className="qb-details-grid">
                  <label className="qb-date-field">
                    <span>
                      Rough date
                    </span>
                    <input
                      type="date"
                      value={dateText}
                      onClick={(event) => {
                        // Open the native calendar when any part of the field is clicked.
                        try {
                          event.currentTarget.showPicker?.();
                        } catch {
                          // Browsers without showPicker keep their native date control.
                        }
                      }}
                      onChange={(event) => setDateText(event.target.value)}
                    />
                  </label>

                  <label className="qb-weekend-toggle">
                    <span className="qb-weekend-copy">
                      <span>
                        Weekend or peak timing?
                      </span>
                      <small>
                        Some rates may run higher.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      checked={weekend}
                      onChange={
                        (event) =>
                          setWeekend(
                            event.target.checked
                          )
                      }
                    />
                    <span
                      className="qb-switch"
                      aria-hidden="true"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="qb-basics-confirm">
              <button
                type="button"
                className="qb-secondary-action"
                onClick={() => goToStep(1)}
              >
                {basicsConfirmed
                  ? 'Basics confirmed · Choose a venue'
                  : 'These details look right · Choose a venue'}
              </button>
            </div>

            <div className="qb-starter-block">
              <div className="qb-starter-heading">
                <div>
                  <span className="qb-section-eyebrow">
                    Not sure where to begin?
                  </span>
                  <h3>
                    Start with a common event shape
                  </h3>
                  <p>
                    These are starting points. You can change anything.
                  </p>
                </div>
              </div>

              {fittingStarters.length === 0 && (
                <p className="qb-starter-empty">
                  None of these starting points fit {formatMoney(targetTotal)} for {headcount} people. Build your own below, or raise your per-person target to see more options.
                </p>
              )}

              <div className="qb-starter-grid">
                {fittingStarters.map(
                  (starter) => {
                    const starterFit =
                      starterFits.get(starter.id);
                    const perPerson = starterFit
                      ? formatRange(
                          starterFit.perPersonLow,
                          starterFit.perPersonHigh
                        )
                      : '';
                    const fitText = !starterFit
                      ? ''
                      : starterFit.fit === 'over'
                        ? `Exceeds your ${formatMoney(targetTotal)} budget · about ${perPerson}/person`
                        : starterFit.fit === 'overlap'
                          ? `May exceed your budget · about ${perPerson}/person`
                          : `Fits your budget · about ${perPerson}/person`;

                    return (
                      <button
                        type="button"
                        key={starter.id}
                        className={
                          starterFit && starterFit.fit === 'over'
                            ? 'is-over-budget'
                            : undefined
                        }
                        onClick={() =>
                          applyStarterPackage(
                            starter
                          )
                        }
                      >
                        <strong>
                          {starter.label}
                        </strong>
                        <span>
                          {starter.detail}
                        </span>
                        {starterFit && (
                          <em
                            className={`qb-starter-fit qb-budget-fit qb-budget-fit-${starterFit.fit}`}
                          >
                            {fitText}
                          </em>
                        )}
                        {starterFit && starterFit.rangeDriver && (
                          <small className="qb-starter-note">
                            The range is wide because of {starterFit.rangeDriver.label} ({formatRange(starterFit.rangeDriver.low, starterFit.rangeDriver.high)}){['benchmark', 'allowance', 'unknown'].includes(starterFit.rangeDriver.confidence) ? ', a Teamward estimate until the vendor confirms' : ''}.
                          </small>
                        )}
                        {starter.budgetNote &&
                          starterFit &&
                          starterFit.fit !== 'within' && (
                            <small className="qb-starter-note">
                              {starter.budgetNote}
                            </small>
                          )}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </section>

          <section
            className="qb-section"
            ref={(element) => {
              stepRefs.current[1] =
                element;
            }}
          >
            <div className="qb-section-heading">
              <span className="qb-section-number">
                02
              </span>
              <h2>Choose a venue</h2>
            </div>

            {venueNotice && (
              <div
                className="qb-venue-notice"
                role="status"
              >
                {venueNotice}
              </div>
            )}

            {selectedVenues.map((selectedVenue) => (
              <div className="qb-selected-venue" key={selectedVenue.id}>
                <div>
                  <span>
                    Selected venue
                  </span>
                  <strong>
                    {selectedVenue.name}
                  </strong>
                  <small>
                    {venuePriceText(selectedVenue)}
                    <PriceSourceTag
                      source={venuePriceSource(selectedVenue)}
                    />
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() => setVenue(selectedVenue.id)}
                >
                  Remove venue
                </button>
              </div>
            ))}

            <div className="qb-recommended-block">
              <div className="qb-recommended-heading">
                <div>
                  <span className="qb-section-eyebrow">
                    Recommended starting points
                  </span>
                  <h3>
                    Strong venue options for this group
                  </h3>
                  <p>
                    Sorted by how well they fit your current planning target.
                  </p>
                </div>
              </div>

              {recommendedVenues.length === 0 && (
                <p className="qb-recommended-empty">
                  No venues fit what&rsquo;s left of your budget. Browse every venue below or raise your target.
                </p>
              )}
              <ul className="qb-recommendation-grid">
                {recommendedVenues.map(
                  (venue) => (
                    <RecommendationCard
                      planKey={`venue:${venue.id}`}
                      key={venue.id}
                      category={
                        venue.category
                      }
                      title={venue.name}
                      price={venuePriceText(venue)}
                      priceSource={venuePriceSource(venue)}
                      budgetFit={
                        budgetFits.venue.get(
                          venue.id
                        ) || 'overlap'
                      }
                      costRange={
                        venueCostRange(
                          venue,
                          headcount
                        )
                      }
                      headcount={headcount}
                      selected={
                        venueIds.includes(venue.id)
                      }
                      selectionType="venue"
                      onSelect={() =>
                        setVenue(
                          venue.id
                        )
                      }
                    />
                  )
                )}
              </ul>
            </div>

            <div className="qb-venue-controls">
              <label className="qb-search">
                <span>
                  Search venues
                </span>
                <input
                  type="search"
                  value={venueQuery}
                  onChange={
                    (event) =>
                      setVenueQuery(
                        event.target.value
                      )
                  }
                  placeholder="Search venue, category, neighborhood…"
                />
              </label>

              <label className="qb-control">
                <span>Denver area</span>
                <select
                  value={venueArea}
                  onChange={
                    (event) => {
                      setVenueArea(
                        event.target.value
                      );
                      setVisibleVenueCount(
                        VENUES_PER_PAGE
                      );
                    }
                  }
                >
                  <option value="all">
                    All Denver areas
                  </option>

                  {DENVER_VENUE_AREAS.map(
                    (area) => (
                      <option
                        key={area}
                        value={area}
                      >
                        {area}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="qb-checkbox">
                <input
                  type="checkbox"
                  checked={outdoorOnly}
                  onChange={
                    (event) => {
                      setOutdoorOnly(
                        event.target.checked
                      );
                      setVisibleVenueCount(
                        VENUES_PER_PAGE
                      );
                    }
                  }
                />
                <span>
                  Outdoor-friendly only
                </span>
              </label>

              <label className="qb-checkbox">
                <input
                  type="checkbox"
                  checked={venueBudgetOnly}
                  onChange={
                    (event) =>
                      setVenueBudgetOnly(
                        event.target.checked
                      )
                  }
                />
                <span>
                  Only show options in my selected range
                </span>
              </label>
            </div>

            <div className="qb-venue-list">
              {groupedVisibleVenues
                .filter(
                  ([, venues]) =>
                    !venueBudgetOnly ||
                    venues.some(
                      (venue) =>
                        budgetFits.venue.get(
                          venue.id
                        ) !== 'over'
                    )
                )
                .map(
                  ([category, venues]) => {
                    const filtered =
                      venueBudgetOnly
                        ? venues.filter(
                            (venue) =>
                              budgetFits.venue.get(
                                venue.id
                              ) !== 'over'
                          )
                        : venues;

                    if (!filtered.length) {
                      return null;
                    }

                    const allOfficeHidden = officeHosted && filtered.every((venue) => venue.id !== 'your-office');
                    const open =
                      openVenueCategory ===
                      category;

                    return (
                      <div
                        className="qb-venue-group"
                        key={category}
                      >
                        <button
                          type="button"
                          className="qb-category-toggle"
                          aria-expanded={open}
                          onClick={() =>
                            setOpenVenueCategory(
                              open
                                ? null
                                : category
                            )
                          }
                        >
                          <span>
                            <strong>
                              {category}
                            </strong>
                            <small>
                              {filtered.length}{' '}
                              option
                              {filtered.length ===
                              1
                                ? ''
                                : 's'}
                              {allOfficeHidden ? ' hidden · Require an off-site location' : ''}
                            </small>
                          </span>
                          <span
                            className="qb-category-chevron"
                            aria-hidden="true"
                          ></span>
                        </button>

                        {open && (
                          <div className="qb-venue-group-list">
                            {allOfficeHidden && <p className="qb-office-explanation">Hidden because you selected “Host at your office”. You can still explore these alternatives.</p>}
                            {filtered.map(
                              (
                                venue
                              ) => (
                                <label
                                  className="qb-option-row"
                                  key={
                                    venue.id
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    name="venue"
                                    checked={
                                      venueIds.includes(venue.id)
                                    }
                                    onChange={() =>
                                      setVenue(
                                        venue.id
                                      )
                                    }
                                  />

                                  <span className="qb-option-copy">
                                    <strong>
                                      {
                                        venue.name
                                      }
                                    </strong>
                                    <span>
                                      {
                                        venue.category
                                      }
                                      {venue.area
                                        ? ` · ${venue.area}`
                                        : ''}
                                    </span>
                                    <span>
                                      Capacity:{' '}
                                      {
                                        venue.capacity
                                      }
                                      {venue.outdoor
                                        ? ' · Outdoor-friendly'
                                        : ''}
                                    </span>
                                    <span>
                                      {
                                        venue.catering
                                      }
                                    </span>
                                  </span>

                                  <span className="qb-option-side">
                                    <span className="qb-price">
                                      {venuePriceText(venue)}
                                      <PriceSourceTag
                                        source={venuePriceSource(venue)}
                                      />
                                    </span>
                                    <BudgetImpactBadge
                                      planKey={`venue:${venue.id}`}
                                      budgetFit={
                                        budgetFits.venue.get(
                                          venue.id
                                        ) ||
                                        'overlap'
                                      }
                                      costRange={
                                        venueCostRange(
                                          venue,
                                          headcount
                                        )
                                      }
                                      headcount={
                                        headcount
                                      }
                                    />
                                  </span>
                                </label>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
            </div>

            {!officeHosted && visibleVenueCount <
              venueResults.length && (
              <button
                type="button"
                className="qb-load-more"
                onClick={() =>
                  setVisibleVenueCount(
                    (count) =>
                      count +
                      VENUES_PER_PAGE
                  )
                }
              >
                Show more venues
              </button>
            )}
          </section>

          <section
            className="qb-section"
            ref={(element) => {
              stepRefs.current[2] =
                element;
            }}
          >
            <div className="qb-section-heading">
              <span className="qb-section-number">
                03
              </span>
              <h2>
                Choose experiences
              </h2>
            </div>

            <div className="qb-recommended-block">
              <div className="qb-recommended-heading">
                <div>
                  <span className="qb-section-eyebrow">
                    Recommended starting points
                  </span>
                  <h3>
                    Strong experience options for this group
                  </h3>
                  <p>
                    Sorted by how well they fit your current planning target.
                  </p>
                </div>
              </div>

              {recommendedActivities.length === 0 && (
                <p className="qb-recommended-empty">
                  No experiences fit what&rsquo;s left of your budget. Browse every experience below or raise your target.
                </p>
              )}
              <ul className="qb-recommendation-grid">
                {recommendedActivities.map(
                  (vendor) => {
                    const rate =
                      rateFor(vendor, { headcount, weekend });
                    const costRange =
                      activityCostRange(
                        rate,
                        headcount
                      );

                    return (
                      <RecommendationCard
                        planKey={`activity:${vendor.id}`}
                        key={vendor.id}
                        category={
                          activityCategoryFor(
                            vendor
                          )
                        }
                        title={vendor.name}
                        price={
                          formatActivityPrice(
                            rate
                          )
                        }
                        priceSource={priceSourceFor(rate && rate.confidence)}
                        budgetFit={
                          budgetFits.activity.get(
                            vendor.id
                          ) || 'overlap'
                        }
                        costRange={costRange}
                        headcount={headcount}
                        selected={
                          selectedVendorIds.has(
                            vendor.id
                          )
                        }
                        selectionType="activity"
                        onSelect={() =>
                          toggleVendor(
                            vendor.id
                          )
                        }
                      />
                    );
                  }
                )}
              </ul>
            </div>

            <div className="qb-list-toolbar">
              <p>All experiences by category</p>
              <SortControl
                id="qb-activity-sort"
                value={activitySort}
                onChange={setActivitySort}
                headcount={headcount}
              />
            </div>

            <div className="qb-activity-categories">
              {activitiesByCategory.map(
                ([category, vendors]) => {
                  const open =
                    openActivityCategories.has(
                      category
                    );

                  const allOfficeHidden = officeHosted && vendors.every((vendor) => !isOfficeActivity(vendor));
                  const categorySelectedCount =
                    vendors.filter(
                      (vendor) =>
                        selectedVendorIds.has(
                          vendor.id
                        )
                    ).length;

                  return (
                    <div
                      className="qb-activity-category"
                      key={category}
                    >
                      <button
                        type="button"
                        className="qb-category-toggle"
                        aria-expanded={open}
                        onClick={() =>
                          setOpenActivityCategories(
                            (previous) => {
                              const next =
                                new Set(
                                  previous
                                );

                              if (
                                next.has(
                                  category
                                )
                              ) {
                                next.delete(
                                  category
                                );
                              } else {
                                next.add(
                                  category
                                );
                              }

                              return next;
                            }
                          )
                        }
                      >
                        <span>
                          <strong>
                            {category}
                          </strong>
                          <small>
                            {
                              vendors.length
                            }{' '}
                            option
                            {vendors.length ===
                            1
                              ? ''
                              : 's'}
                            {allOfficeHidden ? ' hidden · Require an off-site location' : ''}
                            {categorySelectedCount
                              ? ` · ${categorySelectedCount} selected`
                              : ''}
                          </small>
                        </span>
                        <span
                          className="qb-category-chevron"
                          aria-hidden="true"
                        ></span>
                      </button>

                      {open && (
                        <div className="qb-activity-list">
                          {allOfficeHidden && <p className="qb-office-explanation">Hidden because you selected “Host at your office”. You can still explore these alternatives.</p>}
                          <OfficeOptions options={vendors} officeHosted={officeHosted && !allOfficeHidden} compatible={isOfficeActivity}>
                            {(vendor) => {
                              const rate =
                                rateFor(vendor, { headcount, weekend });
                              const costRange =
                                activityCostRange(
                                  rate,
                                  headcount
                                );

                              return (
                                <label
                                  className="qb-option-row qb-option-row-checkbox"
                                  key={
                                    vendor.id
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedVendorIds.has(
                                        vendor.id
                                      )
                                    }
                                    onChange={() =>
                                      toggleVendor(
                                        vendor.id
                                      )
                                    }
                                  />

                                  <span className="qb-option-copy">
                                    <strong>
                                      {
                                        vendor.name
                                      }
                                    </strong>
                                    <span>
                                      {
                                        vendor.setting
                                      }
                                    </span>
                                    <span>
                                      Group:{' '}
                                      {
                                        vendor.groupSize
                                      }
                                      {vendor.duration
                                        ? ` · ${vendor.duration}`
                                        : ''}
                                    </span>
                                  </span>

                                  <span className="qb-option-side">
                                    <span className="qb-price">
                                      {formatActivityPrice(
                                        rate
                                      )}
                                      <PriceSourceTag
                                        source={priceSourceFor(rate && rate.confidence)}
                                      />
                                    </span>
                                    <BudgetImpactBadge
                                      planKey={`activity:${vendor.id}`}
                                      budgetFit={
                                        budgetFits.activity.get(
                                          vendor.id
                                        ) ||
                                        'overlap'
                                      }
                                      costRange={
                                        costRange
                                      }
                                      headcount={
                                        headcount
                                      }
                                    />
                                  </span>
                                </label>
                              );
                            }}
                          </OfficeOptions>
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </section>

          <section
            className="qb-section"
            ref={(element) => {
              stepRefs.current[3] =
                element;
            }}
          >
            <div className="qb-section-heading">
              <span className="qb-section-number">
                04
              </span>
              <h2>
                Food & drinks
              </h2>
            </div>

            {quote.foodBundledBy && (
              <div className="qb-venue-notice qb-food-included-notice" role="status">
                {quote.foodBundledBy} already includes food. Anything you add here stays on your plan but is not added to your total.
              </div>
            )}

            <div className="qb-recommended-block">
              <div className="qb-recommended-heading">
                <div>
                  <span className="qb-section-eyebrow">
                    Recommended starting points
                  </span>
                  <h3>
                    Strong food & drink options for this group
                  </h3>
                  <p>
                    Sorted by how well they fit your current planning target.
                  </p>
                </div>
              </div>

              {recommendedFood.length === 0 && (
                <p className="qb-recommended-empty">
                  No food options fit what&rsquo;s left of your budget. Browse every option below or raise your target.
                </p>
              )}
              <ul className="qb-recommendation-grid">
                {recommendedFood.map(
                  (option) => {
                    const optionQuote =
                      buildQuote({
                        headcount,
                        weekend,
                        venues:
                          selectedVenues,
                        vendors:
                          selectedVendors,
                        foodOptions: [
                          ...FOOD_OPTIONS.filter(
                            (food) =>
                              selectedFoodIds.has(
                                food.id
                              ) &&
                              food.id !==
                                option.id
                          ),
                          option,
                        ],
                        facilitation:
                          selectedFacilitation,
                        planningSupport:
                          selectedPlanningSupport,
                        includeServiceCharge,
                      });
                    const costRange = {
                      low: Math.max(
                        0,
                        optionQuote.totals.low -
                          quoteLow
                      ),
                      high: Math.max(
                        0,
                        optionQuote.totals.high -
                          quoteHigh
                      ),
                    };

                    return (
                      <RecommendationCard
                        planKey={`food:${option.id}`}
                        isFood
                        key={
                          option.id
                        }
                        category={
                          foodDisplayCategory(
                            option.category
                          )
                        }
                        title={option.label}
                        price={
                          formatFoodOptionPrice(
                            option
                          )
                        }
                        priceSource={priceSourceFor(option.confidence)}
                        budgetFit={
                          budgetFits.food.get(
                            option.id
                          ) ||
                          'overlap'
                        }
                        costRange={
                          costRange
                        }
                        headcount={
                          headcount
                        }
                        selected={
                          selectedFoodIds.has(
                            option.id
                          )
                        }
                        selectionType="food"
                        onSelect={() =>
                          toggleFood(
                            option.id
                          )
                        }
                      />
                    );
                  }
                )}
              </ul>
            </div>

            <div className="qb-see-all-food">
              <button
                type="button"
                className="qb-secondary-action"
                aria-expanded={showAllFood}
                onClick={() => setShowAllFood((open) => !open)}
              >
                {showAllFood
                  ? 'Hide the full food & drink list'
                  : `See all ${foodOptionCount} food & drink options${diningExperiences.length ? ` and ${diningExperiences.length} hosted dining experiences` : ''}`}
              </button>
            </div>

            {showAllFood && (
            <>
            <div className="qb-list-toolbar">
              <p>All food &amp; drinks by category</p>
              <SortControl
                id="qb-food-sort"
                value={foodSort}
                onChange={setFoodSort}
                headcount={headcount}
              />
            </div>

            <div className="qb-food-categories">
              {foodCategories.map(
                ([category, options]) => {
                  const open =
                    openFoodCategories.has(
                      category
                    );

                  const allOfficeHidden = officeHosted && options.every((option) => !isOfficeFood(option));
                  const selectedCount =
                    options.filter(
                      (option) =>
                        selectedFoodIds.has(
                          option.id
                        )
                    ).length;

                  return (
                    <div
                      className="qb-food-category"
                      key={category}
                    >
                      <button
                        type="button"
                        className="qb-category-toggle"
                        aria-expanded={open}
                        onClick={() =>
                          setOpenFoodCategories(
                            (previous) => {
                              const next =
                                new Set(
                                  previous
                                );

                              if (
                                next.has(
                                  category
                                )
                              ) {
                                next.delete(
                                  category
                                );
                              } else {
                                next.add(
                                  category
                                );
                              }

                              return next;
                            }
                          )
                        }
                      >
                        <span>
                          <strong>
                            {category}
                          </strong>
                          <small>
                            {
                              options.length
                            }{' '}
                            option
                            {options.length ===
                            1
                              ? ''
                              : 's'}
                            {allOfficeHidden ? ' hidden · Require an off-site location' : ''}
                            {selectedCount
                              ? ` · ${selectedCount} selected`
                              : ''}
                          </small>
                        </span>
                        <span
                          className="qb-category-chevron"
                          aria-hidden="true"
                        ></span>
                      </button>

                      {open && (
                        <div className="qb-food-list">
                          <OfficeOptions options={options} officeHosted={officeHosted && !allOfficeHidden} compatible={isOfficeFood}>
                            {(option) => (
                              <label
                                className="qb-option-row qb-option-row-checkbox"
                                key={
                                  option.id
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedFoodIds.has(
                                      option.id
                                    )
                                  }
                                  onChange={() =>
                                    toggleFood(
                                      option.id
                                    )
                                  }
                                />

                                <span className="qb-option-copy">
                                  <strong>
                                    {
                                      option.name
                                    }
                                  </strong>
                                  <span>
                                    {
                                      option.description
                                    }
                                  </span>
                                  <span>
                                    {formatFoodGroupSize(
                                      option
                                    )}
                                  </span>
                                </span>

                                <span className="qb-option-side">
                                  <span className="qb-price">
                                    {formatFoodOptionPrice(
                                      option
                                    )}
                                    <PriceSourceTag
                                      source={priceSourceFor(option.confidence)}
                                    />
                                  </span>
                                  <BudgetImpactBadge
                                    planKey={`food:${option.id}`}
                                    isFood
                                    budgetFit={
                                      budgetFits.food.get(
                                        option.id
                                      ) ||
                                      'overlap'
                                    }
                                    costRange={
                                      (() => {
                                        const optionQuote =
                                          buildQuote({
                                            headcount,
                                            weekend,
                                            venues:
                                              selectedVenues,
                                            vendors:
                                              selectedVendors,
                                            foodOptions: [
                                              ...FOOD_OPTIONS.filter(
                                                (food) =>
                                                  selectedFoodIds.has(
                                                    food.id
                                                  ) &&
                                                  food.id !==
                                                    option.id
                                              ),
                                              option,
                                            ],
                                            facilitation:
                                              selectedFacilitation,
                                            planningSupport:
                                              selectedPlanningSupport,
                                            includeServiceCharge,
                                          });
                                        return {
                                          low: Math.max(
                                            0,
                                            optionQuote.totals.low -
                                              quoteLow
                                          ),
                                          high: Math.max(
                                            0,
                                            optionQuote.totals.high -
                                              quoteHigh
                                          ),
                                        };
                                      })()
                                    }
                                    headcount={
                                      headcount
                                    }
                                  />
                                </span>
                              </label>
                            )}
                          </OfficeOptions>
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>

            {diningExperiences.length >
              0 && (
              <div className="qb-dining-experiences">
                <div className="qb-recommended-heading">
                  <div>
                    <span className="qb-section-eyebrow">
                      Hosted dining experiences
                    </span>
                    <h3>
                      Looking for food to be the activity?
                    </h3>
                    <p>
                      These are team experiences centered on cooking, dining, or food culture.
                    </p>
                  </div>
                </div>

                <div className="qb-dining-list">
                  <OfficeOptions options={diningExperiences} officeHosted={officeHosted} compatible={isOfficeActivity}>
                    {(vendor) => {
                      const rate =
                        rateFor(vendor, { headcount, weekend });

                      return (
                        <label
                          className="qb-option-row qb-option-row-checkbox"
                          key={vendor.id}
                        >
                          <input
                            type="checkbox"
                            checked={
                              selectedVendorIds.has(
                                vendor.id
                              )
                            }
                            onChange={() =>
                              toggleVendor(
                                vendor.id
                              )
                            }
                          />

                          <span className="qb-option-copy">
                            <strong>
                              {
                                vendor.name
                              }
                            </strong>
                            <span>
                              {
                                vendor.setting
                              }
                            </span>
                            <span>
                              Group:{' '}
                              {
                                vendor.groupSize
                              }
                              {vendor.duration
                                ? ` · ${vendor.duration}`
                                : ''}
                            </span>
                          </span>

                          <span className="qb-option-side">
                            <span className="qb-price">
                              {formatActivityPrice(
                                rate
                              )}
                              <PriceSourceTag
                                source={priceSourceFor(rate && rate.confidence)}
                              />
                            </span>
                          </span>
                        </label>
                      );
                    }}
                  </OfficeOptions>
                </div>
              </div>
            )}
            </>
            )}
          </section>

          <section
            className="qb-section"
            ref={(element) => {
              stepRefs.current[4] =
                element;
            }}
          >
            <div className="qb-section-heading">
              <span className="qb-section-number">
                05
              </span>
              <h2>
                Planning support
              </h2>
            </div>

            <div className="qb-planning-grid">
              <div className="qb-planning-card">
                <div className="qb-field-heading">
                  <div>
                    <h3>
                      Facilitation
                    </h3>
                    <p>
                      Add a facilitator or host if the event needs structured guidance.
                    </p>
                  </div>
                </div>

                <div className="qb-planning-options">
                  {FACILITATION_OPTIONS.map(
                    (option) => (
                      <label
                        className="qb-choice-card"
                        key={option.id}
                      >
                        <input
                          type="radio"
                          name="facilitation"
                          value={option.id}
                          checked={
                            facilitationId ===
                            option.id
                          }
                          onChange={() =>
                            setFacilitationId(
                              option.id
                            )
                          }
                        />
                        <span>
                          <strong>
                            {option.label}
                          </strong>
                          {(option.priceLabel || option.high > 0) && (
                            <span className="qb-option-price">
                              {option.priceLabel ||
                                `${formatRange(option.low, option.high)} flat`}
                              <PriceSourceTag
                                source={priceSourceFor(option.confidence)}
                              />
                            </span>
                          )}
                          <small>
                            {option.description || option.note}
                          </small>
                        </span>
                      </label>
                    )
                  )}
                </div>
              </div>

              <div className="qb-planning-card">
                <div className="qb-field-heading">
                  <div>
                    <h3>
                      Teamward coordination
                    </h3>
                    <p>
                      Choose how much help you want from Teamward.
                    </p>
                  </div>
                </div>

                <div className="qb-planning-options">
                  {PLANNING_SUPPORT_OPTIONS.map(
                    (option) => (
                      <label
                        className="qb-choice-card"
                        key={option.id}
                      >
                        <input
                          type="radio"
                          name="planning-support"
                          value={option.id}
                          checked={
                            planningSupportId ===
                            option.id
                          }
                          onChange={() =>
                            setPlanningSupportId(
                              option.id
                            )
                          }
                        />
                        <span>
                          <strong>
                            {option.label}
                          </strong>
                          {option.priceLabel && (
                            <span className="qb-option-price">
                              {option.priceLabel}
                              <PriceSourceTag
                                source={priceSourceFor(option.confidence, {
                                  free: !option.high,
                                })}
                              />
                            </span>
                          )}
                          <small>
                            {option.description || option.note}
                          </small>
                        </span>
                      </label>
                    )
                  )}
                </div>
              </div>
            </div>
          </section>

          <section
            className="qb-section qb-send-plan"
            id="send-plan"
            ref={leadSectionRef}
            aria-labelledby="qb-send-plan-title"
          >
            <div className="qb-section-heading">
              <span className="qb-section-number">
                06
              </span>
              <h2 id="qb-send-plan-title">
                Send this plan to Teamward for confirmed pricing
              </h2>
            </div>
            <p className="qb-send-plan-lede">
              We&rsquo;ll check availability with each vendor and reply with confirmed pricing. There&rsquo;s no cost to send a plan.
            </p>

            <form className="qb-lead-form" onSubmit={submitLead} noValidate>
              <label className="qb-lead-field">
                <span>Your name</span>
                <input
                  ref={leadNameRef}
                  type="text"
                  autoComplete="name"
                  value={lead.name}
                  onChange={updateLead('name')}
                  aria-invalid={Boolean(leadErrors.name)}
                  aria-describedby={leadErrors.name ? 'qb-lead-name-error' : undefined}
                  required
                />
                {leadErrors.name && (
                  <small id="qb-lead-name-error" className="qb-lead-error">
                    {leadErrors.name}
                  </small>
                )}
              </label>

              <label className="qb-lead-field">
                <span>Work email</span>
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={lead.email}
                  onChange={updateLead('email')}
                  aria-invalid={Boolean(leadErrors.email)}
                  aria-describedby={leadErrors.email ? 'qb-lead-email-error' : undefined}
                  required
                />
                {leadErrors.email && (
                  <small id="qb-lead-email-error" className="qb-lead-error">
                    {leadErrors.email}
                  </small>
                )}
              </label>

              <label className="qb-lead-field">
                <span>Company</span>
                <input
                  type="text"
                  autoComplete="organization"
                  value={lead.company}
                  onChange={updateLead('company')}
                  aria-invalid={Boolean(leadErrors.company)}
                  aria-describedby={leadErrors.company ? 'qb-lead-company-error' : undefined}
                  required
                />
                {leadErrors.company && (
                  <small id="qb-lead-company-error" className="qb-lead-error">
                    {leadErrors.company}
                  </small>
                )}
              </label>

              <label className="qb-lead-field">
                <span>Event date</span>
                <input
                  type="date"
                  value={lead.date}
                  onChange={updateLead('date')}
                  aria-invalid={Boolean(leadErrors.date)}
                  aria-describedby={leadErrors.date ? 'qb-lead-date-error' : undefined}
                  required
                />
                {leadErrors.date && (
                  <small id="qb-lead-date-error" className="qb-lead-error">
                    {leadErrors.date}
                  </small>
                )}
              </label>

              <button
                type="submit"
                className="qb-primary-action qb-lead-submit"
                disabled={!hasPackageItems}
                aria-describedby={!hasPackageItems ? 'qb-lead-send-hint' : undefined}
              >
                Send this plan to Teamward
              </button>

              {!hasPackageItems && (
                <p className="qb-send-hint" id="qb-lead-send-hint">
                  Pick a venue or experience to see your estimate.
                </p>
              )}

              <p className="qb-lead-fine-print" role="status">
                {leadStatus === 'opened'
                  ? 'Your request opened in a new tab with this plan filled in. Press Complete there to send it.'
                  : 'Next, your request opens with this plan filled in. Press Complete to send it.'}
              </p>
            </form>
          </section>
        </div>

        <aside
          className="qb-summary"
          aria-label="Estimated package budget"
          ref={summaryRef}
        >
          {renderSummary('aside')}
        </aside>
      </div>

      {!summaryInView && !sheetOpen && (
        <button
          type="button"
          className="qb-floating-summary"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
        >
          <span>
            Estimate
            <small>Tap to view breakdown</small>
          </span>
          <strong>
            {hasPackageItems
              ? formatRange(
                  quote.totals.low,
                  quote.totals.high
                )
              : 'Start building'}
          </strong>
        </button>
      )}

      {sheetOpen && (
        <div className="qb-sheet-layer">
          <button
            type="button"
            className="qb-sheet-backdrop"
            aria-label="Close estimate"
            tabIndex={-1}
            onClick={() => setSheetOpen(false)}
          />
          <div
            className="qb-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Estimated budget"
          >
            <button
              type="button"
              className="qb-sheet-close"
              ref={sheetCloseRef}
              onClick={() => setSheetOpen(false)}
            >
              Close
            </button>
            {renderSummary('sheet')}
          </div>
        </div>
      )}
    </main>
    </PlanTotalsContext.Provider>
  );
};

export default QuoteBuilderPage;
